import React, { useState, useEffect } from 'react';
import SafeIcon from '../common/SafeIcon';
import { FiTarget, FiBox, FiPieChart, FiCoffee, FiPlus } from 'react-icons/fi';
import { FOOD_LIBRARY } from '../data/foodLibrary';

const MacroCard = ({ label, grams, calories, pct, color, icon, suggestedFood }) => (
  <div className="flex flex-col gap-4">
    <div className="bg-gray-50 dark:bg-gray-900/50 p-5 rounded-2xl border border-gray-100 dark:border-gray-700/50">
      <div className="flex items-center justify-between mb-4">
        <div className={`w-10 h-10 rounded-xl ${color} bg-opacity-10 dark:bg-opacity-20 flex items-center justify-center`}>
          <SafeIcon icon={icon} className={color.replace('bg-', 'text-')} />
        </div>
        <span className="text-[10px] font-black uppercase tracking-widest text-gray-400">{pct}%</span>
      </div>
      <div className="space-y-1">
        <h4 className="text-sm font-bold text-gray-500 uppercase tracking-tight">{label}</h4>
        <div className="text-2xl font-black tracking-tighter dark:text-white">{grams}g</div>
        <div className="text-[10px] font-medium text-gray-400 uppercase tracking-widest">{calories} kcal</div>
      </div>
    </div>
    
    {/* Dynamic Food Suggestion */}
    <div className="px-4 py-3 bg-white dark:bg-gray-800/30 rounded-xl border border-dashed border-gray-200 dark:border-gray-700">
      <div className="flex items-center justify-between mb-2">
        <span className="text-[9px] font-black text-blue-600 uppercase tracking-widest">Suggested Fuel</span>
        <span className="text-[9px] font-bold text-gray-400">{suggestedFood.amount}</span>
      </div>
      <div className="text-xs font-bold dark:text-gray-200 mb-1">{suggestedFood.name}</div>
      <div className="flex items-center justify-between">
        <span className="text-[10px] text-emerald-500 font-bold">{suggestedFood.cal} kcal</span>
        <div className="group relative">
          <SafeIcon icon={FiCoffee} className="w-3 h-3 text-gray-300 cursor-help" />
          <div className="absolute bottom-full mb-2 right-0 w-32 p-2 bg-gray-900 text-[9px] text-white rounded-lg opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-10 shadow-xl">
            {suggestedFood.tip}
          </div>
        </div>
      </div>
    </div>
  </div>
);

const MacroBreakdown = ({ macros }) => {
  const [randomFoods, setRandomFoods] = useState(null);

  useEffect(() => {
    const getRandom = (type) => {
      const items = FOOD_LIBRARY[type];
      return items[Math.floor(Math.random() * items.length)];
    };

    setRandomFoods({
      protein: getRandom('protein'),
      carbs: getRandom('carbs'),
      fat: getRandom('fat')
    });
  }, [macros]);

  if (!macros || !randomFoods) return null;

  return (
    <div className="space-y-8 mt-12 pt-12 border-t border-gray-50 dark:border-gray-800">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-1.5 h-6 bg-blue-600 rounded-full" />
          <h3 className="text-xl font-black tracking-tight uppercase">Nutrient Architecture</h3>
        </div>
        <div className="hidden md:block text-[9px] font-black text-gray-400 uppercase tracking-[0.2em]">
          Dynamic Recommendations Based on Profile
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <MacroCard 
          label="Protein" 
          grams={macros.protein.grams} 
          calories={macros.protein.cal} 
          pct={macros.protein.pct} 
          color="bg-blue-500" 
          icon={FiTarget}
          suggestedFood={randomFoods.protein}
        />
        <MacroCard 
          label="Carbohydrates" 
          grams={macros.carbs.grams} 
          calories={macros.carbs.cal} 
          pct={macros.carbs.pct} 
          color="bg-emerald-500" 
          icon={FiBox}
          suggestedFood={randomFoods.carbs}
        />
        <MacroCard 
          label="Healthy Fats" 
          grams={macros.fat.grams} 
          calories={macros.fat.cal} 
          pct={macros.fat.pct} 
          color="bg-amber-500" 
          icon={FiPieChart}
          suggestedFood={randomFoods.fat}
        />
      </div>

      <div className="bg-blue-50 dark:bg-blue-900/10 rounded-2xl p-6 flex flex-col md:flex-row items-center justify-between gap-4 border border-blue-100 dark:border-blue-800/30">
        <div>
          <h4 className="font-bold text-blue-900 dark:text-blue-200 flex items-center gap-2">
            Per Meal Distribution
            <span className="px-1.5 py-0.5 rounded bg-blue-100 dark:bg-blue-900 text-[8px] uppercase">4 Meals</span>
          </h4>
          <p className="text-xs text-blue-700 dark:text-blue-400/70">Optimized for thermic efficiency and satiety</p>
        </div>
        <div className="flex gap-6">
          <div className="text-center">
            <div className="text-[10px] font-black text-blue-400 uppercase tracking-widest">P</div>
            <div className="font-black text-blue-900 dark:text-blue-100">{macros.perMeal.p}g</div>
          </div>
          <div className="text-center">
            <div className="text-[10px] font-black text-blue-400 uppercase tracking-widest">C</div>
            <div className="font-black text-blue-900 dark:text-blue-100">{macros.perMeal.c}g</div>
          </div>
          <div className="text-center">
            <div className="text-[10px] font-black text-blue-400 uppercase tracking-widest">F</div>
            <div className="font-black text-blue-900 dark:text-blue-100">{macros.perMeal.f}g</div>
          </div>
          <div className="h-8 w-px bg-blue-200 dark:bg-blue-800" />
          <div className="text-center">
            <div className="text-[10px] font-black text-blue-400 uppercase tracking-widest">Kcal</div>
            <div className="font-black text-blue-900 dark:text-blue-100">{macros.perMeal.cal}</div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MacroBreakdown;